// VizSim dataset authoring and release.
import {
  AddVizSimDatasetCropsRequest,
  AddVizSimDatasetCropsResponse,
  EnsureVizSimDatasetRequest,
  ReleasedVizSimCropIdsResponse,
  ReleaseVizSimDatasetRequest,
  RemoveVizSimDatasetCropsResponse,
  UpdateVizSimDatasetCropRequest,
  VizSimDatasetCrop,
  VizSimDatasetDetailResponse,
  VizSimDatasetListResponse,
  VizSimDatasetTagSuggestionsRequest,
  VizSimDatasetTagSuggestionsResponse,
} from '../../types/vizSimDatasets';
import { forgeRequest } from '../forgeRequest';
import { FORGE_VIZSIM_DATASETS_API_URL } from './endpoints';

export async function fetchVizSimDatasets(
  token: string,
  filters?: {
    chainId?: number | null;
    page?: number;
    limit?: number;
  }
): Promise<VizSimDatasetListResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.page) params.set('page', String(filters.page));
  if (filters?.limit) params.set('limit', String(filters.limit));
  const query = params.toString();
  return forgeRequest<VizSimDatasetListResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}${query ? `?${query}` : ''}`,
    'Error fetching VizSim datasets'
  );
}

export async function fetchVizSimDataset(
  token: string,
  datasetId: string,
  filters?: {
    releaseMajor?: number | null;
    releaseMinor?: number | null;
    label?: string | null;
    tag?: string | null;
    classHash?: string | null;
    samplePage?: number;
    sampleLimit?: number;
  }
): Promise<VizSimDatasetDetailResponse> {
  const params = new URLSearchParams();
  if (filters?.releaseMajor !== undefined && filters.releaseMajor !== null) {
    params.set('release_major', String(filters.releaseMajor));
  }
  if (filters?.releaseMinor !== undefined && filters.releaseMinor !== null) {
    params.set('release_minor', String(filters.releaseMinor));
  }
  if (filters?.label) params.set('label', filters.label);
  if (filters?.tag) params.set('tag', filters.tag);
  if (filters?.classHash) params.set('class_hash', filters.classHash);
  if (filters?.samplePage) params.set('sample_page', String(filters.samplePage));
  if (filters?.sampleLimit) params.set('sample_limit', String(filters.sampleLimit));
  const query = params.toString();
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}${query ? `?${query}` : ''}`,
    'Error fetching VizSim dataset'
  );
}

export async function createVizSimDataset(
  token: string,
  request: EnsureVizSimDatasetRequest
): Promise<VizSimDatasetDetailResponse> {
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    FORGE_VIZSIM_DATASETS_API_URL,
    'Error creating VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function addVizSimDatasetCrops(
  token: string,
  datasetId: string,
  request: AddVizSimDatasetCropsRequest
): Promise<AddVizSimDatasetCropsResponse> {
  return forgeRequest<AddVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops`,
    'Error adding crops to VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function fetchVizSimDatasetTagSuggestions(
  token: string,
  request: VizSimDatasetTagSuggestionsRequest
): Promise<VizSimDatasetTagSuggestionsResponse> {
  return forgeRequest<VizSimDatasetTagSuggestionsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/tag-suggestions`,
    'Error fetching VizSim tag suggestions',
    { method: 'POST', body: request }
  );
}

export async function addVizSimDatasetChainCrops(
  token: string,
  chainId: number,
  request: AddVizSimDatasetCropsRequest
): Promise<AddVizSimDatasetCropsResponse> {
  return forgeRequest<AddVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/chains/${encodeURIComponent(String(chainId))}/crops`,
    'Error adding crops to VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function updateVizSimDatasetCrop(
  token: string,
  datasetId: string,
  cropId: string,
  request: UpdateVizSimDatasetCropRequest
): Promise<VizSimDatasetCrop> {
  return forgeRequest<VizSimDatasetCrop>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
      cropId
    )}`,
    'Error updating VizSim dataset crop',
    { method: 'PATCH', body: request }
  );
}

export async function removeVizSimDatasetCrop(
  token: string,
  datasetId: string,
  cropId: string,
  scope: 'crop' | 'class'
): Promise<RemoveVizSimDatasetCropsResponse> {
  const params = new URLSearchParams({ scope });
  return forgeRequest<RemoveVizSimDatasetCropsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/crops/${encodeURIComponent(
      cropId
    )}?${params.toString()}`,
    'Error removing VizSim dataset crop',
    { method: 'DELETE' }
  );
}

export async function releaseVizSimDataset(
  token: string,
  datasetId: string,
  request: ReleaseVizSimDatasetRequest
): Promise<VizSimDatasetDetailResponse> {
  return forgeRequest<VizSimDatasetDetailResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/${encodeURIComponent(datasetId)}/releases`,
    'Error releasing VizSim dataset',
    { method: 'POST', body: request }
  );
}

export async function fetchReleasedVizSimCropIds(
  token: string,
  filters?: {
    chainId?: number | null;
    datasetId?: string | null;
    label?: string | null;
    tag?: string | null;
  }
): Promise<ReleasedVizSimCropIdsResponse> {
  const params = new URLSearchParams();
  if (filters?.chainId) params.set('chain_id', String(filters.chainId));
  if (filters?.datasetId) params.set('dataset_id', filters.datasetId);
  if (filters?.label) params.set('label', filters.label);
  if (filters?.tag) params.set('tag', filters.tag);
  const query = params.toString();
  return forgeRequest<ReleasedVizSimCropIdsResponse>(
    token,
    `${FORGE_VIZSIM_DATASETS_API_URL}/released-crop-ids${query ? `?${query}` : ''}`,
    'Error fetching released VizSim crop ids'
  );
}
